import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Shield, Clock, Copy, Monitor, AlertTriangle, Unplug } from 'lucide-react';

const UserLicensesPage = () => {
  const queryClient = useQueryClient();

  const { data: licenses = [], isLoading } = useQuery({
    queryKey: ['userLicenses'],
    queryFn: () => axiosClient.get('/account/licenses')
  });

  // ✅ NEW: Unbind Mutation
  const unbindMutation = useMutation({
      mutationFn: (key) => axiosClient.post(`/user/licenses/${key}/unbind`),
      onSuccess: () => {
          alert("Device Unbound! You can now use this key on another machine.");
          queryClient.invalidateQueries(['userLicenses']);
      },
      onError: () => alert("Failed to reset license.")
  });

  const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
      alert("License Key Copied!");
  };

  const getDaysRemaining = (expiryDate) => {
      if (!expiryDate) return "Lifetime";
      const diff = new Date(expiryDate) - new Date();
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return days > 0 ? `${days} Days` : "Expired";
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <h1 className="text-2xl font-bold text-slate-800">My Licenses</h1>
      
      {isLoading ? <div className="p-10 text-center text-slate-400">Loading Licenses...</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {licenses.map((l, i) => {
            const daysLeft = getDaysRemaining(l.expires_at);
            const isExpired = daysLeft === "Expired";
            const isActive = !l.revoked && !isExpired && l.activated; // Check if bound

            return (
                <div key={i} className={`p-6 rounded-xl border shadow-sm relative overflow-hidden transition-all hover:shadow-md ${isActive ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
                    
                    {/* Status Badge */}
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold uppercase ${
                        l.revoked ? 'bg-red-100 text-red-700' :
                        isActive ? 'bg-green-100 text-green-700' : 
                        'bg-amber-100 text-amber-700'
                    }`}>
                        {l.revoked ? 'Revoked' : isActive ? 'Active' : 'Unused'}
                    </div>

                    <div className="flex items-center mb-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-500'}`}>
                            <Shield className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-slate-800">{l.package_name || 'Standard License'}</h3>
                            <p className="text-sm text-slate-500 flex items-center">
                               <Monitor className="w-3 h-3 mr-1"/> {l.device_limit} Device Limit
                            </p>
                        </div>
                    </div>

                    <div className="bg-slate-100 p-3 rounded-lg border border-slate-200 flex justify-between items-center mb-4">
                        <code className="font-mono text-slate-700 font-bold tracking-wider select-all">{l.license_key}</code>
                        <button onClick={() => handleCopy(l.license_key)} className="text-blue-600 hover:text-blue-800"><Copy className="w-4 h-4"/></button>
                    </div>

                    <div className="flex justify-between items-center text-sm pt-2 border-t border-slate-50">
                        <div className="flex items-center text-slate-500">
                           <Clock className="w-4 h-4 mr-2"/>
                           {isExpired ? <span className="text-red-500 font-bold">Expired</span> : <span>Expires in <span className="font-bold text-slate-700">{daysLeft}</span></span>}
                        </div>
                        
                        {/* ✅ UNBIND BUTTON (Only show if active) */}
                        {isActive && (
                            <button 
                                onClick={() => { if(window.confirm("Disconnect current device? This will stop protection on that machine.")) unbindMutation.mutate(l.license_key) }}
                                className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                            >
                                <Unplug className="w-3 h-3 mr-1"/> Reset Device
                            </button>
                        )}
                    </div>
                </div>
            );
        })}
      </div>
      
      {licenses.length === 0 && !isLoading && (
          <div className="text-center p-10 bg-white rounded-xl border border-dashed border-slate-300">
             <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
             <h3 className="text-slate-600 font-bold">No Licenses Found</h3>
             <p className="text-slate-400 text-sm">Purchase a plan to get protected.</p>
          </div>
      )}
    </div>
  );
};

export default UserLicensesPage;